import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Injectable, signal } from '@angular/core';

type PinRecord = {
  kdf: 'PBKDF2';
  iterations: number;
  saltB64: string;
  hashB64: string;
};

type LockStatus = {
  isLocked: boolean;
  lockedUntil: number | null;
  remainingMs: number;
  failedAttempts: number;
};

function isPinRecord(value: unknown): value is PinRecord {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    record['kdf'] === 'PBKDF2' &&
    typeof record['iterations'] === 'number' &&
    typeof record['saltB64'] === 'string' &&
    record['saltB64'].length > 0 &&
    typeof record['hashB64'] === 'string' &&
    record['hashB64'].length > 0
  );
}

function parsePinRecord(value: unknown): unknown {
  return typeof value === 'string' ? JSON.parse(value) : value;
}

const PIN_KEY = 'pin.v1';
const FAIL_KEY = 'lock.failedAttempts';
const UNTIL_KEY = 'lock.lockedUntil';
const BIOMETRIC_ENABLED_KEY = 'biometric.enabled';

@Injectable({
  providedIn: 'root',
})
export class PinAuthService {
  private readonly _isAuthenticated = signal(false);
  public readonly isAuthenticated = this._isAuthenticated.asReadonly();

  private _justSetup = false;

  public async isPinSet(): Promise<boolean> {
    try {
      const raw = await SecureStorage.get(PIN_KEY);
      if (raw === null || raw === undefined) return false;

      if (!isPinRecord(parsePinRecord(raw))) {
        await SecureStorage.remove(PIN_KEY);
        return false;
      }

      return true;
    } catch {
      try {
        await SecureStorage.remove(PIN_KEY);
      } catch {
        return false;
      }

      return false;
    }
  }

  public async isBiometricEnabled(): Promise<boolean> {
    try {
      const result = await SecureStorage.get(BIOMETRIC_ENABLED_KEY);
      return result === true;
    } catch {
      return false;
    }
  }

  public async setBiometricEnabled(enabled: boolean): Promise<void> {
    await SecureStorage.set(BIOMETRIC_ENABLED_KEY, enabled);
  }

  public async setPin(pin: string): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iterations = 150_000;

    const hash = await this.pbkdf2Hash(pin, salt, iterations);

    const rec: PinRecord = {
      kdf: 'PBKDF2',
      iterations,
      saltB64: this.b64(salt),
      hashB64: this.b64(hash),
    };

    await SecureStorage.set(PIN_KEY, JSON.stringify(rec));
    await SecureStorage.set(FAIL_KEY, 0);
    await SecureStorage.set(UNTIL_KEY, 0);

    this._justSetup = true;
    this._isAuthenticated.set(true);
  }

  public wasJustSetup(): boolean {
    return this._justSetup;
  }

  public clearJustSetupFlag(): void {
    this._justSetup = false;
  }

  public async getLockStatus(): Promise<LockStatus> {
    const lockedUntil = await this.getNumber(UNTIL_KEY);
    const failedAttempts = await this.getNumber(FAIL_KEY);
    const now = Date.now();

    if (lockedUntil && lockedUntil > now) {
      return {
        isLocked: true,
        lockedUntil,
        remainingMs: lockedUntil - now,
        failedAttempts,
      };
    }

    return {
      isLocked: false,
      lockedUntil: null,
      remainingMs: 0,
      failedAttempts,
    };
  }

  public async verifyPin(
    pin: string,
  ): Promise<{ success: boolean; lockStatus: LockStatus }> {
    const lockStatus = await this.getLockStatus();
    if (lockStatus.isLocked) {
      return { success: false, lockStatus };
    }

    const recVal = await SecureStorage.get(PIN_KEY);

    if (recVal === null || recVal === undefined) {
      return { success: false, lockStatus };
    }

    const parsed = parsePinRecord(recVal);

    if (!isPinRecord(parsed)) {
      return { success: false, lockStatus };
    }

    const rec = parsed;
    const salt = this.ub64(rec.saltB64);
    const hash = await this.pbkdf2Hash(pin, salt, rec.iterations);
    const ok = this.b64(hash) === rec.hashB64;

    if (ok) {
      await SecureStorage.set(FAIL_KEY, 0);
      await SecureStorage.set(UNTIL_KEY, 0);
      this._isAuthenticated.set(true);
      return { success: true, lockStatus: await this.getLockStatus() };
    }

    const fails = lockStatus.failedAttempts + 1;
    await SecureStorage.set(FAIL_KEY, fails);

    let delayMs = 0;
    if (fails >= 10) delayMs = 5 * 60_000;
    else if (fails >= 5) delayMs = 30_000;

    let newLockedUntil: number | null = null;
    if (delayMs > 0) {
      newLockedUntil = Date.now() + delayMs;
      await SecureStorage.set(UNTIL_KEY, newLockedUntil);
    }

    return {
      success: false,
      lockStatus: {
        isLocked: delayMs > 0,
        lockedUntil: newLockedUntil,
        remainingMs: delayMs,
        failedAttempts: fails,
      },
    };
  }

  public authenticateWithBiometric(): void {
    this._isAuthenticated.set(true);
  }

  public logout(): void {
    this._isAuthenticated.set(false);
    this._justSetup = false;
  }

  public async resetAll(): Promise<void> {
    await SecureStorage.remove(PIN_KEY);
    await SecureStorage.remove(FAIL_KEY);
    await SecureStorage.remove(UNTIL_KEY);
    await SecureStorage.remove(BIOMETRIC_ENABLED_KEY);
    this._isAuthenticated.set(false);
    this._justSetup = false;
  }

  private async getNumber(key: string): Promise<number> {
    try {
      const value = await SecureStorage.get(key);

      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
      }

      return 0;
    } catch {
      return 0;
    }
  }

  private b64(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes));
  }

  private ub64(s: string): Uint8Array {
    return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
  }

  private async pbkdf2Hash(
    pin: string,
    salt: Uint8Array,
    iterations: number,
  ): Promise<Uint8Array> {
    const textEncoder = new TextEncoder();

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      textEncoder.encode(pin),
      { name: 'PBKDF2' },
      false,
      ['deriveBits'],
    );

    const bits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt,
        iterations,
      },
      keyMaterial,
      256,
    );

    return new Uint8Array(bits);
  }
}
